import { useDeliveryLocationCookie } from '@/components/content/Header/parts/DeliveryLocator/hooks/useDeliveryLocationCookie';
import { openLocateStoreMenu } from '@/components/content/Header/parts/LocateStore/utils/locateStoreEvents';
import { deliveryMethodSelectorSX } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/styles/deliveryMethodSelector';
import {
	to12Hour,
	isBeforeCutoff,
} from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/utils/to12Hour';
import { usePhysicalStoreData } from '@/data/Content/usePhysicalStoreData';
import { useDeliveryLocationPanel } from '@/data/context/deliveryLocationPanelCtx';
import { PdpHomeDeliveryOption } from '@/data/Content/PdpHomeDeliveryOption';
import { PdpPickupOption } from '@/data/Content/PdpPickupOption';
import { useLocalization } from '@/data/Localization';
import { Box, Link, Typography } from '@mui/material';
import { FC, MouseEvent } from 'react';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';
import { useMobileApp } from '@/utils/useMobileApp';
import { logEvent } from '@/data/utils/logEventsUtils';

type Props = {
	isDeliverySelected: boolean;
	physicalStoreId: string;
	pickup: PdpPickupOption | PdpHomeDeliveryOption;
	delivery: PdpHomeDeliveryOption;
};

// `same-day` (163024) reads like a regular home delivery; its `cutoffTime` is
// EDD_SAMEDAY_CUTOFF_TIME, set by `usePdpHomeDeliveryOption`.
const VISIBLE_STATUSES: string[] = ['with-date', 'same-day', 'available-no-date'];

export const DeliveryContextFooter: FC<Props> = ({
	isDeliverySelected,
	physicalStoreId,
	pickup,
	delivery,
}) => {
	const { DeliveryMethodSelector: labels } = useLocalization('HDMProductDetail');
	const { storeName } = usePhysicalStoreData({ physicalStoreId }) || {};
	const { zipCode } = useDeliveryLocationCookie();
	const { openDeliveryPanel } = useDeliveryLocationPanel();
	const { isMobileApp } = useMobileApp();

	const active = isDeliverySelected ? delivery : pickup;
	const cutoffTime = active.cutoffTime ?? '';
	const isMarketFulfilled = !!active.isMarketFulfilled;
	// Dropship / Special Order read "order today" and don't depend on the cutoff time.
	const showCutoffLegend = isMarketFulfilled || isBeforeCutoff(cutoffTime);

	if (!VISIBLE_STATUSES.includes(active.status)) {
		return null;
	}

	const handleChangeStore = (event: MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		if (isMobileApp) {
			logEvent('openStoreSelector');
		} else {
			openLocateStoreMenu();
		}
	};

	const handleChangeZipCode = (event: MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		if (isMobileApp) {
			logEvent('openZipCodeSelector');
		} else {
			openDeliveryPanel();
		}
	};

	const footerContent = isDeliverySelected
		? {
				prefix: labels.DeliveryPrefix.t(),
				linkId: 'deliveryChangeZipCode',
				linkValue: zipCode,
				onChangeLink: handleChangeZipCode,
				showCost: !!delivery.freeShipping,
		  }
		: {
				prefix: labels.PickupPrefix.t(),
				linkId: 'deliveryChangeStore',
				linkValue: storeName,
				onChangeLink: handleChangeStore,
				showCost: true,
		  };

	return (
		<Box id="deliveryContextFooter" sx={deliveryMethodSelectorSX.footer}>
			<Box sx={deliveryMethodSelectorSX.footerRow}>
				<Typography component="span" variant="bodyBaseline" color="grey.500">
					{footerContent.prefix}
				</Typography>
				<Link
					id={footerContent.linkId}
					component="button"
					type="button"
					underline="hover"
					onClick={footerContent.onChangeLink}
					sx={deliveryMethodSelectorSX.footerLink}
				>
					{footerContent.linkValue}
				</Link>
				{footerContent.showCost && (
					<>
						<Typography component="span" sx={deliveryMethodSelectorSX.footerSeparator}>
							{'|'}
						</Typography>
						<Typography
							component="span"
							variant="bodyBaseline"
							className="delivery-selector__cost delivery-selector__cost--free"
							sx={deliveryMethodSelectorSX.footerCostFree}
							fontWeight={OpenSansFontWeights.semibold}
						>
							{labels.Free.t()}
						</Typography>
					</>
				)}
			</Box>
			{active.hasCutoffFootnote && active.cutoffTime && showCutoffLegend && (
				<Typography
					variant="bodySubText"
					className="delivery-selector__cutoff"
					sx={deliveryMethodSelectorSX.footerLegal}
				>
					{isMarketFulfilled ? (
						labels.MarketFulfilledFootnote.t()
					) : (
						<>
							{labels.CutoffFootnote.t()}{' '}
							<Typography
								component="span"
								variant="bodySubText"
								fontWeight={OpenSansFontWeights.semibold}
							>
								{to12Hour(active.cutoffTime)}
							</Typography>
						</>
					)}
				</Typography>
			)}
			{active.status === 'available-no-date' && (
				<Typography
					variant="bodySubText"
					className="delivery-selector__no-date-message"
					sx={deliveryMethodSelectorSX.footerNoDateMessage}
				>
					{isDeliverySelected ? labels.NoDeliveryDateMessage.t() : labels.NoPickupDateMessage.t()}
				</Typography>
			)}
		</Box>
	);
};
